import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import ModalDialog from '@/components/common/ModalDialog'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { TriangleAlertIcon } from 'lucide-react'
import EthHashInfo from '@/components/common/EthHashInfo'
import NameInput from '@/components/common/NameInput'
import { cn } from '@/utils/cn'
import SimilarAddressAlert from './SimilarAddressAlert'
import type { SimilarAddressInfo } from '../../hooks/useNonPinnedSafeWarning.types'

interface AddTrustedSafeDialogProps {
  open: boolean
  safeAddress: string
  safeName?: string
  chainId: string
  hasSimilarAddress: boolean
  similarAddresses: SimilarAddressInfo[]
  onConfirm: (name: string) => void
  onCancel: () => void
}

interface FormData {
  name: string
}

/**
 * Confirmation dialog for adding a safe to the trusted list
 * Shows enhanced warning if similar addresses are detected
 */
const AddTrustedSafeDialog = ({
  open,
  safeAddress,
  safeName,
  hasSimilarAddress,
  similarAddresses,
  onConfirm,
  onCancel,
}: AddTrustedSafeDialogProps) => {
  const methods = useForm<FormData>({
    defaultValues: {
      name: safeName || '',
    },
    mode: 'onChange',
  })

  const { handleSubmit, formState, reset } = methods

  // Reset form when the target Safe changes so stale names aren't submitted
  useEffect(() => {
    reset({ name: safeName || '' })
  }, [safeName, safeAddress, reset])

  const onSubmit = handleSubmit((data: FormData) => {
    onConfirm(data.name.trim() || '')
  })

  return (
    <ModalDialog
      open={open}
      maxWidth="sm"
      fullWidth
      data-testid="add-trusted-safe-dialog"
      dialogTitle="Add to my accounts"
      hideChainIndicator
    >
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <div className="p-6">
            {hasSimilarAddress && <SimilarAddressAlert similarAddresses={similarAddresses} />}

            <div className="mb-4">
              <Typography variant="paragraph-small" color="muted" className="mb-2 block">
                Safe to add
              </Typography>
              <div
                className={cn(
                  'bg-background border-border rounded-md',
                  hasSimilarAddress ? 'border-2 p-4' : 'border p-4',
                )}
              >
                <EthHashInfo address={safeAddress} showCopyButton shortAddress={false} showAvatar avatarSize={32} />
              </div>
            </div>

            {!hasSimilarAddress && (
              <Typography variant="paragraph-small" color="muted" className="mb-4 block">
                Review the full address above. Continue only if you recognize this Safe and want to add it to your
                accounts.
              </Typography>
            )}

            <div className="mb-4">
              <NameInput
                data-testid="safe-name-input"
                name="name"
                label="Safe name"
                placeholder="Enter a name for this Safe"
                autoFocus
              />
            </div>
          </div>

          <div className="flex justify-between gap-2 p-6 pt-0">
            <Button onClick={onCancel} variant="outline">
              Cancel
            </Button>
            <Button type="submit" data-testid="confirm-add-trusted-safe-button" disabled={!formState.isValid}>
              {hasSimilarAddress && <TriangleAlertIcon className="size-4" />}
              {hasSimilarAddress ? 'I understand, add anyway' : 'Confirm'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </ModalDialog>
  )
}

export default AddTrustedSafeDialog
